import React from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  Position,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useStore,
} from 'reactflow';
import {
  JOURNEY_NODE_HEIGHT,
  JOURNEY_NODE_WIDTH,
} from '../../../../support/journeyMap/constants';

type AnchorSide = 'top' | 'right' | 'bottom' | 'left';

function getNearestSide(params: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}): AnchorSide {
  const { sourceX, sourceY, targetX, targetY } = params;
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;

  if (Math.abs(dx) > Math.abs(dy)) {
    return dx >= 0 ? 'right' : 'left';
  }

  return dy >= 0 ? 'bottom' : 'top';
}

function getAnchorPoint(
  nodeX: number,
  nodeY: number,
  side: AnchorSide,
) {
  switch (side) {
    case 'top':
      return {
        x: nodeX + JOURNEY_NODE_WIDTH / 2,
        y: nodeY,
        position: Position.Top,
      };
    case 'right':
      return {
        x: nodeX + JOURNEY_NODE_WIDTH,
        y: nodeY + JOURNEY_NODE_HEIGHT / 2,
        position: Position.Right,
      };
    case 'bottom':
      return {
        x: nodeX + JOURNEY_NODE_WIDTH / 2,
        y: nodeY + JOURNEY_NODE_HEIGHT,
        position: Position.Bottom,
      };
    case 'left':
    default:
      return {
        x: nodeX,
        y: nodeY + JOURNEY_NODE_HEIGHT / 2,
        position: Position.Left,
      };
  }
}

export default function JourneyMapCanvasEdge(props: EdgeProps) {
  const {
    id,
    source,
    target,
    style,
    markerEnd,
    label,
    data,
    selected,
  } = props;

  const sourceNode = useStore((store) => store.nodeInternals.get(source));
  const targetNode = useStore((store) => store.nodeInternals.get(target));

  if (!sourceNode || !targetNode) {
    return null;
  }

  const sourceX =
    sourceNode.positionAbsolute?.x ?? sourceNode.position?.x ?? 0;
  const sourceY =
    sourceNode.positionAbsolute?.y ?? sourceNode.position?.y ?? 0;

  const targetX =
    targetNode.positionAbsolute?.x ?? targetNode.position?.x ?? 0;
  const targetY =
    targetNode.positionAbsolute?.y ?? targetNode.position?.y ?? 0;

  const sourceCenterX = sourceX + JOURNEY_NODE_WIDTH / 2;
  const sourceCenterY = sourceY + JOURNEY_NODE_HEIGHT / 2;
  const targetCenterX = targetX + JOURNEY_NODE_WIDTH / 2;
  const targetCenterY = targetY + JOURNEY_NODE_HEIGHT / 2;

  const sourceSide = getNearestSide({
    sourceX: sourceCenterX,
    sourceY: sourceCenterY,
    targetX: targetCenterX,
    targetY: targetCenterY,
  });

  const targetSide = getNearestSide({
    sourceX: targetCenterX,
    sourceY: targetCenterY,
    targetX: sourceCenterX,
    targetY: sourceCenterY,
  });

  const sourceAnchor = getAnchorPoint(sourceX, sourceY, sourceSide);
  const targetAnchor = getAnchorPoint(targetX, targetY, targetSide);

  const edgeShape = String(data?.edgeShape || 'smoothstep');
  const strokeWidth = Number(data?.strokeWidth ?? style?.strokeWidth ?? 2);
  const strokeDasharray = data?.strokeDasharray ?? style?.strokeDasharray;
  const stroke = data?.stroke || style?.stroke || '#656C75';

  let edgePath = '';
  let labelX = (sourceAnchor.x + targetAnchor.x) / 2;
  let labelY = (sourceAnchor.y + targetAnchor.y) / 2;

  if (edgeShape === 'straight') {
    const result = getStraightPath({
      sourceX: sourceAnchor.x,
      sourceY: sourceAnchor.y,
      targetX: targetAnchor.x,
      targetY: targetAnchor.y,
    });
    edgePath = result[0];
    labelX = result[1];
    labelY = result[2];
  } else if (edgeShape === 'bezier') {
    const result = getBezierPath({
      sourceX: sourceAnchor.x,
      sourceY: sourceAnchor.y,
      sourcePosition: sourceAnchor.position,
      targetX: targetAnchor.x,
      targetY: targetAnchor.y,
      targetPosition: targetAnchor.position,
    });
    edgePath = result[0];
    labelX = result[1];
    labelY = result[2];
  } else {
    const result = getSmoothStepPath({
      sourceX: sourceAnchor.x,
      sourceY: sourceAnchor.y,
      sourcePosition: sourceAnchor.position,
      targetX: targetAnchor.x,
      targetY: targetAnchor.y,
      targetPosition: targetAnchor.position,
      borderRadius: edgeShape === 'step' ? 0 : 16,
    });
    edgePath = result[0];
    labelX = result[1];
    labelY = result[2];
  }

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke,
          strokeWidth,
          strokeDasharray,
          opacity: selected ? 1 : 0.95,
        }}
      />

      {label ? (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              fontSize: 12,
              fontWeight: 700,
              color: stroke,
              background: 'rgba(255,255,255,0.96)',
              border: `1px solid ${stroke}`,
              borderRadius: 8,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
            }}
            className="nodrag nopan"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}